/** Values for a message's `{name}` placeholders. */
export type MessageParams = Record<string, string | number>;

/** The placeholder names in a message type: `"{count} menu untuk “{query}”"` → `"count" | "query"`. */
export type PlaceholderOf<S extends string> = S extends `${string}{${infer Name}}${infer Rest}`
  ? Name | PlaceholderOf<Rest>
  : never;

/** A dictionary's source language (Indonesian), typed `as const` so its keys and placeholders are known. */
export type Dictionary = Readonly<Record<string, string>>;

/**
 * Another language's version of dictionary `D`: every key, plus an optional `<key>.one` form for a count
 * the language calls singular ("1 item" vs "2 items").
 */
export type MessagesOf<D extends Dictionary> = Record<keyof D & string, string> &
  Partial<Record<`${keyof D & string}.one`, string>>;

/**
 * `t(key)`, or `t(key, params)` when the message has placeholders: the params are type-checked against
 * the Indonesian message, so a missing `{name}` fails typecheck.
 */
export type TranslateOf<D extends Dictionary> = <K extends keyof D & string>(
  key: K,
  ...params: [PlaceholderOf<D[K]>] extends [never] ? [] : [Record<PlaceholderOf<D[K]>, string | number>]
) => string;

/** Keys of `D` whose message has no placeholders: what a label map or a route's crumb may hold. */
export type PlainKeyOf<D extends Dictionary> = {
  [K in keyof D & string]: [PlaceholderOf<D[K]>] extends [never] ? K : never;
}[keyof D & string];

const PLACEHOLDER = /\{(\w+)\}/g;

const pluralRules = new Map<string, Intl.PluralRules>();

/** Whether `count` takes the singular form in `intlLocale` ("1 item", but "0 items"). */
function singular(count: number, intlLocale: string): boolean {
  let rules = pluralRules.get(intlLocale);
  if (!rules) {
    rules = new Intl.PluralRules(intlLocale);
    pluralRules.set(intlLocale, rules);
  }
  return rules.select(count) === "one";
}

/**
 * The message for `key` with its `{name}` placeholders filled in one pass (so a value containing
 * braces is never filled again). A placeholder without a value is left as is, visible in review.
 * With a numeric `count` that `intlLocale` calls singular, the `<key>.one` form is used when the
 * dictionary has one (English does; Indonesian nouns don't change).
 */
export function translate<M extends Record<string, string>>(
  messages: M,
  key: keyof M & string,
  params?: MessageParams,
  intlLocale?: string,
): string {
  const count = params?.count;
  const one =
    typeof count === "number" && intlLocale && singular(count, intlLocale)
      ? messages[`${key}.one`]
      : undefined;
  const message = one ?? messages[key] ?? key;
  if (!params) return message;
  return message.replace(PLACEHOLDER, (whole, name: string) =>
    name in params ? String(params[name]) : whole,
  );
}

/** A message's placeholder names, sorted and unique: both languages of a key must agree on them. */
export function placeholders(message: string): string[] {
  return [...new Set([...message.matchAll(PLACEHOLDER)].map((m) => m[1] ?? ""))].sort();
}
